/**
 * The audit ledger.
 *
 * The README's third promise: when a client asks *"who touched this box, when, and what did they
 * do"*, there is an answer. This screen is where that answer is read.
 *
 * Two things it does that an ordinary activity feed does not:
 *
 * 1. **It shows the chain state at the top, before the entries.** A tamper-evident record nobody
 *    ever verifies is tamper-evident in principle and unchecked in practice, so the check runs on
 *    load rather than behind a button somebody has to think to press.
 * 2. **It shows each entry's hash.** Published so a reader can verify the chain independently of
 *    this service — which is the whole difference between a hash chain and a log you are asked to
 *    trust.
 */

import type { JSX } from 'react';

import { api, type AuditEntry } from '../shared/api/client';
import {
  Badge,
  Card,
  ErrorBox,
  Formula,
  PageHeader,
  Spinner,
  Stat,
  StatRow,
  Table,
  type Tone,
} from '../shared/ui/ui';
import { useAsync } from '../shared/ui/useAsync';

/** Plain-language names, so the ledger reads as events rather than as identifiers. */
const EVENT_LABEL: Record<string, string> = {
  'auth.sign_in_succeeded': 'Signed in',
  'auth.signed_out': 'Signed out',
  'ticket.created': 'Ticket raised',
  'ticket.state_changed': 'Ticket state changed',
  'config.captured': 'Configuration captured',
  'config.raw_read': 'Raw configuration read',
  'telemetry.captured': 'Telemetry captured',
  'telemetry.raw_read': 'Raw telemetry read',
  'collector.enrolled': 'Collector enrolled',
  'collector.revoked': 'Collector revoked',
  'vulnerability.imported': 'Scan imported',
  'risk_config.changed': 'Risk methodology changed',
  'campaign.created': 'Campaign planned',
  'campaign.findings_added': 'Findings added to campaign',
  'campaign.state_changed': 'Campaign state changed',
};

/**
 * Reading raw device text is toned as a caution, not because it is wrong — engineers need it —
 * but because it is the event an incident responder scans for first.
 */
function eventTone(eventType: string): Tone {
  if (eventType.endsWith('.raw_read')) return 'warn';
  if (eventType === 'collector.revoked' || eventType === 'risk_config.changed') return 'info';
  return 'muted';
}

export function Audit(): JSX.Element {
  const { data, error, loading } = useAsync(() => api.audit(200));
  const verification = useAsync(() => api.verifyAudit());

  const entries = data ?? [];
  const rawReads = entries.filter((e) => e.event_type.endsWith('.raw_read')).length;
  const actors = new Set(entries.map((e) => e.actor_id).filter(Boolean)).size;

  const chain = verification.data;

  return (
    <>
      <PageHeader
        title="Audit ledger"
        subtitle="Who did what, when. Append-only, hash-chained, and verified on every load rather than on request."
      />

      <ErrorBox error={error} />
      <ErrorBox error={verification.error} />

      <Card>
        <StatRow>
          <Stat
            label="Chain"
            value={verification.loading ? '…' : chain?.intact === true ? 'intact' : 'BROKEN'}
            tone={verification.loading ? 'neutral' : chain?.intact === true ? 'ok' : 'danger'}
            hint={
              chain?.intact === true
                ? 'every entry hashes to its recorded value'
                : 'an entry was altered or removed'
            }
          />
          <Stat label="Entries shown" value={entries.length} />
          <Stat label="Distinct actors" value={actors} />
          <Stat
            label="Raw text reads"
            value={rawReads}
            tone={rawReads > 0 ? 'warn' : 'neutral'}
            hint="device configuration is a credential store"
          />
        </StatRow>

        {chain !== null && !chain.intact && (
          <div className="alert alert--danger">
            <strong>The ledger does not verify.</strong> {chain.broken}
            {chain.at_entry !== null && <> Entry {chain.at_entry}.</>}
          </div>
        )}
      </Card>

      <Card
        title="Events"
        subtitle="Newest first. Each entry publishes its hash, so the chain can be checked without trusting this screen."
      >
        {loading ? (
          <Spinner />
        ) : (
          <Table
            columns={['#', 'When', 'Event', 'Subject', 'Detail', 'Hash']}
            empty="The ledger is empty. Every mutation appends to it, so this fills as the platform is used."
          >
            {entries.map((e) => (
              <tr key={e.id}>
                <td className="table__num">{e.id}</td>
                <td>
                  <Formula>{e.occurred_at.replace('T', ' ').slice(0, 19)}</Formula>
                </td>
                <td>
                  <div className="table__strong">
                    {EVENT_LABEL[e.event_type] ?? e.event_type}
                  </div>
                  <Badge tone={eventTone(e.event_type)}>{e.event_type}</Badge>
                </td>
                <td>
                  {e.subject_kind === null ? (
                    '—'
                  ) : (
                    <Formula>
                      {e.subject_kind}
                      {e.subject_id === null ? '' : ` ${e.subject_id.slice(0, 8)}`}
                    </Formula>
                  )}
                </td>
                <td>
                  <Formula>{renderDetail(e)}</Formula>
                </td>
                <td>
                  {/* Truncated for the eye; the full value is in the API response, which is what
                      an independent verifier would read anyway. */}
                  <Formula>{e.entry_hash.slice(0, 12)}…</Formula>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </>
  );
}

/** Renders the structured facts as `key=value` pairs, which is how they were stored. */
function renderDetail(entry: AuditEntry): string {
  const pairs = Object.entries(entry.detail);
  if (pairs.length === 0) return '—';
  return pairs.map(([k, v]) => `${k}=${String(v)}`).join('  ');
}
